<template>
  <div>
    <section class="ar-panel ar-panel--add">
      <header class="ar-panel__header">
        <div class="ar-panel__title">
          <feather-icon
            icon="UserPlusIcon"
            size="18"
          />
          {{ config.addTitle }}
        </div>
        <span class="ar-panel__hint">
          {{ config.addHint }}
        </span>
      </header>
      <div class="ar-add">
        <v-select
          v-model="selectedUser"
          :options="availableUsers"
          :disabled="addLoad || usersLoading"
          :loading="usersLoading"
          placeholder="Search user by name or email…"
          class="ar-add__select"
        >
          <template #no-options>
            No more users available to add
          </template>
        </v-select>
        <v-select
          v-if="hasScopeCodes"
          v-model="selectedScopeCode"
          :options="scopeCodeOptions"
          :disabled="addLoad || scopeCodesLoading"
          :loading="scopeCodesLoading"
          placeholder="Select scope code…"
          class="ar-add__select"
        >
          <template #no-options>
            No scope codes available
          </template>
        </v-select>
        <button
          type="button"
          class="ar-btn ar-btn--primary"
          :disabled="!canAdd"
          @click="addMember"
        >
          <b-spinner
            v-if="addLoad"
            small
            variant="light"
          />
          <template v-else>
            <feather-icon
              icon="PlusIcon"
              size="16"
            />
            Add
          </template>
        </button>
      </div>
    </section>

    <section class="ar-panel">
      <header class="ar-panel__header ar-panel__header--row">
        <div class="ar-panel__title">
          <feather-icon
            icon="ClipboardIcon"
            size="18"
          />
          {{ config.listTitle }}
        </div>
        <div class="ar-search">
          <feather-icon
            icon="SearchIcon"
            size="15"
            class="ar-search__icon"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="ar-search__input"
            placeholder="Search by name or email…"
          >
          <button
            v-if="searchQuery"
            type="button"
            class="ar-search__clear"
            @click="searchQuery = ''"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </button>
        </div>
      </header>

      <div
        v-if="dataLoad"
        class="ar-loading"
      >
        <div class="ar-loading__spinner" />
        <p class="mb-0 text-muted">
          Loading {{ config.memberPlural }}…
        </p>
      </div>

      <div
        v-else-if="!filteredMembers.length"
        class="ar-empty"
      >
        <div class="ar-empty__icon">
          <feather-icon
            icon="InboxIcon"
            size="28"
          />
        </div>
        <h5 class="mb-50">
          {{ searchQuery ? config.emptySearchTitle : config.emptyTitle }}
        </h5>
        <p class="mb-0 text-muted">
          {{ searchQuery ? 'Try a different search.' : config.emptyHint }}
        </p>
      </div>

      <div
        v-else
        class="ar-grid"
        :class="{ 'ar-grid--wide': hasScopeCodes }"
      >
        <article
          v-for="(member, index) in filteredMembers"
          :key="member.id || member.user_id"
          class="ar-card"
        >
          <div
            class="ar-card__accent"
            :class="`ar-card__accent--${index % 4}`"
          />
          <div class="ar-card__top">
            <div
              class="ar-card__avatar"
              :class="`ar-card__avatar--${index % 4}`"
            >
              {{ avatarText(member.name) }}
            </div>
            <button
              v-if="!hasMemberScopes(member)"
              type="button"
              class="ar-card__delete"
              :title="config.removeButtonTitle"
              :disabled="removeLoadId === member.id"
              @click="confirmRemove(member)"
            >
              <b-spinner
                v-if="removeLoadId === member.id"
                small
                variant="danger"
              />
              <feather-icon
                v-else
                icon="Trash2Icon"
                size="16"
              />
            </button>
          </div>
          <h4 class="ar-card__name">
            {{ member.name }}
          </h4>
          <a
            :href="`mailto:${member.email}`"
            class="ar-card__email"
          >
            <feather-icon
              icon="MailIcon"
              size="14"
            />
            {{ member.email }}
          </a>
          <div
            v-if="hasMemberScopes(member)"
            class="ar-scopes"
          >
            <div class="ar-scopes__head">
              <span class="ar-scopes__title">
                <feather-icon
                  icon="LayersIcon"
                  size="14"
                />
                Scope codes
                <span class="ar-scopes__count">{{ member.scope_codes.length }}</span>
              </span>
              <button
                v-if="member.scope_codes.length > SCOPES_PREVIEW"
                type="button"
                class="ar-scopes__toggle"
                @click="toggleScopes(member)"
              >
                {{ isScopesExpanded(member) ? 'Show less' : 'Show all' }}
                <feather-icon
                  :icon="isScopesExpanded(member) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                  size="14"
                />
              </button>
            </div>

            <div
              v-if="isScopesExpanded(member) && member.scope_codes.length > SCOPES_PREVIEW"
              class="ar-scopes__filter"
            >
              <feather-icon
                icon="SearchIcon"
                size="13"
                class="ar-scopes__filter-icon"
              />
              <input
                type="text"
                class="ar-scopes__filter-input"
                placeholder="Filter scope codes…"
                :value="scopeFilters[member.user_id] || ''"
                @input="setScopeFilter(member, $event.target.value)"
              >
            </div>

            <ul
              class="ar-scope-list"
              :class="{ 'is-expanded': isScopesExpanded(member) }"
            >
              <li
                v-for="scope in visibleScopes(member)"
                :key="scope.id"
                class="ar-scope-item"
              >
                <span class="ar-scope-item__code">{{ scope.scope_code }}</span>
                <span
                  class="ar-scope-item__name"
                  :title="scope.scope_name"
                >
                  {{ scope.scope_name }}
                </span>
                <button
                  type="button"
                  class="ar-scope-item__delete"
                  :title="config.removeButtonTitle"
                  :disabled="removeLoadId === scope.id"
                  @click="confirmRemoveScope(member, scope)"
                >
                  <b-spinner
                    v-if="removeLoadId === scope.id"
                    small
                    variant="danger"
                  />
                  <feather-icon
                    v-else
                    icon="XIcon"
                    size="14"
                  />
                </button>
              </li>
              <li
                v-if="!visibleScopes(member).length"
                class="ar-scope-empty"
              >
                No matching scope codes
              </li>
            </ul>
          </div>
          <div
            v-if="member.created_at"
            class="ar-card__meta"
          >
            <span class="ar-card__chip">
              <feather-icon
                icon="CalendarIcon"
                size="12"
              />
              {{ formatDateValue(member.created_at) }}
            </span>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import VSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import formatDate from '@/global-compositions/formatDate'

export default {
  name: 'AssignPersonalPanel',
  components: {
    'v-select': VSelect,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
    usersLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      members: [],
      dataLoad: false,
      addLoad: false,
      removeLoadId: null,
      selectedUser: null,
      selectedScopeCode: null,
      scopeCodes: [],
      scopeCodesLoading: false,
      searchQuery: '',
      SCOPES_PREVIEW: 3,
      expandedScopes: {},
      scopeFilters: {},
    }
  },
  computed: {
    hasScopeCodes() {
      return Boolean(this.config.scopeCodesEndpoint)
    },
    scopeCodeOptions() {
      return this.scopeCodes.map(item => ({
        value: item.id,
        label: item.name,
      }))
    },
    canAdd() {
      if (!this.selectedUser || this.addLoad) return false
      if (this.hasScopeCodes && !this.selectedScopeCode) return false
      return true
    },
    memberList() {
      return Array.isArray(this.members) ? this.members : []
    },
    filteredMembers() {
      const q = this.searchQuery.trim().toLowerCase()
      if (!q) return this.memberList
      return this.memberList.filter(item => (
        String(item.name || '').toLowerCase().includes(q)
        || String(item.email || '').toLowerCase().includes(q)
      ))
    },
    existingUserIds() {
      // A user may be assigned several scope codes, so don't hide existing users in that mode
      if (this.hasScopeCodes) return new Set()
      return new Set(this.memberList.map(item => Number(item.user_id)).filter(Boolean))
    },
    availableUsers() {
      const list = this.$store.getters['generalData/assignList/assignList'] || []
      return list
        .filter(user => !this.existingUserIds.has(Number(user?.value?.id)))
        .map(user => ({
          value: user.value.id,
          label: user.label,
        }))
    },
  },
  mounted() {
    this.fetchMembers()
    if (this.hasScopeCodes) this.fetchScopeCodes()
  },
  methods: {
    formatDateValue(value) {
      return formatDate(value) || value
    },
    avatarText(name) {
      if (!name) return '?'
      return String(name)
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('')
    },
    normalizeList(payloadData) {
      if (Array.isArray(payloadData)) return payloadData
      if (Array.isArray(payloadData?.data)) return payloadData.data
      return Object.values(payloadData || {}).find(Array.isArray) || []
    },
    async fetchMembers() {
      this.dataLoad = true
      try {
        const response = await axios.get(this.config.endpoint)
        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          this.members = this.normalizeList(responseData?.data)
        }
      } catch (error) {
        this.members = []
        this.showError(error, this.config.loadError)
      } finally {
        this.dataLoad = false
      }
    },
    async fetchScopeCodes() {
      this.scopeCodesLoading = true
      try {
        const response = await axios.get(this.config.scopeCodesEndpoint)
        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          this.scopeCodes = this.normalizeList(responseData?.data)
        }
      } catch (error) {
        this.scopeCodes = []
        this.showError(error, 'Failed to load scope codes')
      } finally {
        this.scopeCodesLoading = false
      }
    },
    async addMember() {
      const userId = this.selectedUser?.value
      if (!userId || this.addLoad) return

      const payload = { user_id: userId }
      if (this.hasScopeCodes) {
        const scopeCodeId = this.selectedScopeCode?.value
        if (!scopeCodeId) return
        payload.scope_code_id = scopeCodeId
      }

      this.addLoad = true
      try {
        const response = await axios.post(this.config.endpoint, payload)
        if (response.status === 200 || response.status === 201) {
          this.selectedUser = null
          this.selectedScopeCode = null
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.config.addSuccess,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          await this.fetchMembers()
        }
      } catch (error) {
        this.showError(error, this.config.addError)
      } finally {
        this.addLoad = false
      }
    },
    hasMemberScopes(member) {
      return Array.isArray(member.scope_codes)
    },
    isScopesExpanded(member) {
      return Boolean(this.expandedScopes[member.user_id])
    },
    toggleScopes(member) {
      this.$set(this.expandedScopes, member.user_id, !this.expandedScopes[member.user_id])
    },
    setScopeFilter(member, value) {
      this.$set(this.scopeFilters, member.user_id, value)
    },
    visibleScopes(member) {
      const list = member.scope_codes || []
      if (!this.isScopesExpanded(member)) return list.slice(0, this.SCOPES_PREVIEW)
      const q = String(this.scopeFilters[member.user_id] || '').trim().toLowerCase()
      if (!q) return list
      return list.filter(scope => (
        String(scope.scope_code || '').toLowerCase().includes(q)
        || String(scope.scope_name || '').toLowerCase().includes(q)
      ))
    },
    confirmRemoveScope(member, scope) {
      this.$swal({
        title: this.config.removeTitle,
        text: `${member.name} - ${scope.scope_code}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#ea5455',
        cancelButtonColor: '#82868b',
      }).then(result => {
        if (result.isConfirmed) {
          this.removeScope(member, scope)
        }
      })
    },
    async removeScope(member, scope) {
      this.removeLoadId = scope.id
      try {
        const response = await axios.delete(`${this.config.endpoint}/${scope.id}`)
        if (response.status === 200 || response.status === 204) {
          this.members = this.memberList
            .map(item => (item.user_id === member.user_id
              ? { ...item, scope_codes: item.scope_codes.filter(s => s.id !== scope.id) }
              : item))
            .filter(item => !Array.isArray(item.scope_codes) || item.scope_codes.length)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.config.removeSuccess,
              icon: 'Trash2Icon',
              variant: 'success',
            },
          })
        }
      } catch (error) {
        this.showError(error, this.config.removeError)
      } finally {
        this.removeLoadId = null
      }
    },
    confirmRemove(member) {
      this.$swal({
        title: this.config.removeTitle,
        text: member.name,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#ea5455',
        cancelButtonColor: '#82868b',
      }).then(result => {
        if (result.isConfirmed) {
          this.removeMember(member)
        }
      })
    },
    async removeMember(member) {
      this.removeLoadId = member.id
      try {
        const response = await axios.delete(`${this.config.endpoint}/${member.id}`)
        if (response.status === 200 || response.status === 204) {
          this.members = this.memberList.filter(item => item.id !== member.id)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.config.removeSuccess,
              icon: 'Trash2Icon',
              variant: 'success',
            },
          })
        }
      } catch (error) {
        this.showError(error, this.config.removeError)
      } finally {
        this.removeLoadId = null
      }
    },
    showError(error, fallback) {
      let message = fallback
      try {
        if (error?.response?.data?.payload) {
          const payload = decryptData(error.response.data.payload)
          const msg = payload?.message || payload
          if (typeof msg === 'string') message = msg
          else if (typeof msg === 'object') {
            const firstList = Object.values(msg).find(Array.isArray)
            if (firstList) {
              const [firstMessage] = firstList
              if (firstMessage) message = firstMessage
            }
          }
        }
      } catch {
        // keep fallback
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: 'BellIcon',
          variant: 'danger',
        },
      })
    },
  },
}
</script>
